import React, { type JSX } from 'react';
import { CircleCheck, X } from 'lucide-react';

import { PortaledLayer } from '../../shared/PortaledLayer';
import {
  areAllPackagingCommandStepsComplete,
  countCompletedPackagingCommandSteps,
  PACKAGING_COMMAND_STEPS,
  type PackagingCommandChecklistState,
  type PackagingCommandStepKey,
} from '../utils/raceDeviceCommandChecklist';
import type { RaceConfigurationRequirements } from '../../../helpers/types/DeviceValidationTypes';

type Props = {
  open: boolean;
  raceConfiguration: RaceConfigurationRequirements | null;
  checklist: PackagingCommandChecklistState;
  onToggleStep: (key: PackagingCommandStepKey) => void;
  onClose: () => void;
  /** Called once every step is confirmed and the operator chooses to proceed — opens the real report. */
  onContinue: () => void;
};

/**
 * Gates the validation report behind the Packaging device-commands checklist:
 * opened from the "Verify devices" CTA (or the forward-stage gate), it must have
 * every step ticked before "Run validation report" is enabled.
 */
export default function DeviceCommandsGateDialog(props: Props): JSX.Element | null {
  const { open, raceConfiguration, checklist, onToggleStep, onClose, onContinue } = props;
  if (!open) return null;

  const completed = countCompletedPackagingCommandSteps(checklist);
  const total = PACKAGING_COMMAND_STEPS.length;
  const allComplete = areAllPackagingCommandStepsComplete(checklist);
  const fwLabel = String(raceConfiguration?.fw_version ?? '').trim();
  const fwFrontLabel = String(raceConfiguration?.fw_version_front ?? '').trim();

  return (
    <PortaledLayer>
      <div
        data-race-stacked-overlay=""
        className="fixed inset-0 z-[5100] flex items-center justify-center bg-black/50 p-3 dark:bg-black/70 sm:p-6"
        role="presentation"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="flex max-h-[min(92vh,760px)] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm device commands"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Confirm device commands</div>
              <div className="mt-0.5 text-[11px] text-gray-500 dark:text-slate-400">
                {allComplete ? 'All commands confirmed.' : `${completed}/${total} confirmed`}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-slate-400 dark:hover:bg-slate-700"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
            <p className="rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2 text-xs leading-relaxed text-blue-950 dark:border-blue-800/60 dark:bg-blue-950/30 dark:text-blue-100">
              These commands are what get devices ready to pass verification. Send each fleet command from the device
              list or command form, then tick the box once it has been submitted for every device on this race — once
              all items below are confirmed, every device should pass the validation report.
            </p>
            <p className="text-xs leading-relaxed text-gray-600 dark:text-slate-300">
              For log clears, you may upload and download logs first if needed.
            </p>
            <div className="grid grid-cols-1 gap-1 text-[11px] font-medium text-gray-700 dark:text-slate-200 sm:grid-cols-2">
              {fwLabel ? (
                <p>
                  Rear firmware target: <span className="font-mono">{fwLabel}</span>
                </p>
              ) : (
                <p className="text-amber-800 dark:text-amber-200">Set rear firmware in race configuration.</p>
              )}
              {fwFrontLabel ? (
                <p>
                  Front firmware target: <span className="font-mono">{fwFrontLabel}</span>
                </p>
              ) : (
                <p className="text-amber-800 dark:text-amber-200">Set front firmware in race configuration.</p>
              )}
            </div>
            <ul className="space-y-2">
              {PACKAGING_COMMAND_STEPS.map((step) => {
                const checked = checklist[step.key] === true;
                const inputId = `packaging-cmd-gate-${step.key}`;
                return (
                  <li
                    key={step.key}
                    className={[
                      'rounded-lg border px-3 py-2.5 transition-colors',
                      checked
                        ? 'border-emerald-200 bg-emerald-50/80 dark:border-emerald-800/50 dark:bg-emerald-950/25'
                        : 'border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900/50',
                    ].join(' ')}
                  >
                    <label htmlFor={inputId} className="flex cursor-pointer items-start gap-3">
                      <input
                        id={inputId}
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggleStep(step.key)}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold text-gray-900 dark:text-slate-100">
                          {step.label}
                        </span>
                        <span className="mt-0.5 block text-[11px] leading-relaxed text-gray-600 dark:text-slate-400">
                          {step.description}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onContinue}
              disabled={!allComplete}
              title={allComplete ? undefined : `Confirm all ${total} commands to continue`}
              className={[
                'inline-flex items-center gap-2 rounded-md border px-4 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                allComplete
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100'
                  : 'border-gray-300 bg-gray-100 text-gray-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400',
              ].join(' ')}
            >
              {allComplete ? <CircleCheck className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden /> : null}
              Run validation report
            </button>
          </div>
        </div>
      </div>
    </PortaledLayer>
  );
}
